import React, { useRef, useEffect } from 'react';
import ModalManager from './ModalManager';
import MacroCard from './MacroCard';
import FoodLogList from './FoodLogList';
import InputBar from './InputBar';
import ManualEntryForm from './ManualEntryForm';
import DatePicker from './DatePicker';

const DashboardLayout = ({  // Alert props
  scanLoading,
  scanError,
  loading,
  imageLoading,
  error,
    // Modal props
  showBarcodeModal,
  setShowBarcodeModal,
  handleBarcodeDetected,
  scannedProductData,
  showScannedProduct,
  onConfirmProduct,
  onRetryScanning,
  showScanHistory,
  setShowScanHistory,
  scanHistory,
  showCameraModal,
  setShowCameraModal,
  cameraResOptions,
  cameraResolution,
  setCameraResolution,
  webcamRef,
  handleCapture,
  showGalleryModal,
  setShowGalleryModal,
  getRootProps,
  getInputProps,
  isDragActive,
  
  // Header props
  selectedDate,
  setSelectedDate,
  getMacroSummary,
  getTargets,
  
  // Content props
  foodLog,
  editMealId,
  editMealData,
  handleEdit,
  handleEditChange,
  handleEditSave,
  handleEditCancel,
  handleDelete,
  handleMacroEdit,
  
  // Input bar props
  prompt,
  setPrompt,
  handlePromptSubmit,
  handleGallery,
  handleCamera,
  fileInputRef,
  handleImageUpload,
  
  // Manual entry props
  manualEntry,
  handleManualSubmit,
}) => {    const scrollContainerRef = useRef(null);
  
  // Auto-scroll to bottom when new entries are added
  useEffect(() => {
    if (scrollContainerRef.current && foodLog && (
      (Array.isArray(foodLog.food) && foodLog.food.length > 0) || 
      (Array.isArray(foodLog.exercise) && foodLog.exercise.length > 0)
    )) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const scrollElement = scrollContainerRef.current;
          scrollElement.scrollTo({
            top: scrollElement.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [foodLog]);return (
    <div className="h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col overflow-hidden">
      {/* Scan feedback alerts - Keep outside for barcode scanning */}
      {scanLoading && (
        <div className="alert alert-info text-center">
          Scanning barcode...
        </div>      )}
      {scanError && (
        <div className="alert alert-danger text-center">
          {scanError}
        </div>
      )}
      
      {/* General error alerts - Keep outside for visibility */}
      {error && (
        <div className="alert alert-danger mt-2">
          {error}
        </div>
      )}

      {/* Modals */}      <ModalManager
        showBarcodeModal={showBarcodeModal}
        setShowBarcodeModal={setShowBarcodeModal}
        handleBarcodeDetected={handleBarcodeDetected}
        scanLoading={scanLoading}
        scanError={scanError}
        scannedProductData={scannedProductData}
        showScannedProduct={showScannedProduct}
        onConfirmProduct={onConfirmProduct}
        onRetryScanning={onRetryScanning}
        showScanHistory={showScanHistory}
        setShowScanHistory={setShowScanHistory}
        scanHistory={scanHistory}
        showCameraModal={showCameraModal}
        setShowCameraModal={setShowCameraModal}
        cameraResOptions={cameraResOptions}
        cameraResolution={cameraResolution}
        setCameraResolution={setCameraResolution}
        webcamRef={webcamRef}
        handleCapture={handleCapture}
        showGalleryModal={showGalleryModal}
        setShowGalleryModal={setShowGalleryModal}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        isDragActive={isDragActive}
      />      
      {/* Main Content - Full Screen Layout */}
      <div className="flex-1 flex pt-1">
        <div className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row gap-6 p-6">          {/* Left Column - Combined Date Picker & Macro Cards */}
          <div className="lg:w-1/2 flex flex-col">
            
            {/* Combined Date Picker and Daily Macro Overview */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/20 p-6 hover:shadow-xl transition-all duration-300 flex flex-col h-[calc(100vh-8rem)]">
              {/* Date Selection Header */}
              <div className="flex items-center justify-between mb-6 flex-shrink-0">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <span className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-3 text-white text-sm">
                    📅
                  </span>
                  Select Date
                </h2>                <div className="text-lg font-medium text-gray-600">
                  {(() => {
                    // Parse date in local timezone to avoid UTC shift
                    const [year, month, day] = selectedDate.split('-').map(Number);
                    const localDate = new Date(year, month - 1, day);
                    return localDate.toLocaleDateString('en-US', { 
                      weekday: 'short',
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    });
                  })()}
                </div>
              </div>
                {/* Date Picker Container */}
              <div className="mb-4 flex-shrink-0">
                <DatePicker 
                  selectedDate={selectedDate} 
                  onSelect={setSelectedDate} 
                  swipeable 
                  fillMonth 
                />
              </div>

              {/* Daily Macro Overview - Takes remaining space */}
              <div className="flex-1 flex flex-col">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center mr-3 text-white text-sm">
                    📊
                  </span>
                  Daily Macro Overview
                </h3>
                <div className="flex-1">
                  <MacroCard 
                    macros={getMacroSummary()} 
                    targets={getTargets()} 
                  />
                </div>
              </div>
            </div>
          </div>          {/* Right Column - AI Food Assistant */}
          <div className="lg:w-1/2 flex flex-col">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/20 p-3 hover:shadow-xl transition-all duration-300 flex flex-col h-[calc(100vh-8rem)]">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <span className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center mr-3 text-white">
                  🤖
                </span>
                AI Food Assistant
              </h2>
              
              {/* Loading alerts inside AI Food Assistant */}
              {loading && (
                <div className="alert alert-info mb-4">
                  Loading nutrition facts...
                </div>
              )}
              {imageLoading && (
                <div className="alert alert-info mb-4">
                  Processing image...
                </div>
              )}              {/* Food Log List - Fixed Height with Scrollbar */}
              <div className="flex-1 min-h-0">
                <div className="h-full flex flex-col">
                  {(!foodLog || ((Array.isArray(foodLog.food) && foodLog.food.length === 0) && (Array.isArray(foodLog.exercise) && foodLog.exercise.length === 0))) ? (
                    <div className="h-full flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="bi bi-chat-dots text-2xl text-gray-400"></i>
                        </div>
                        <p className="text-sm font-medium">No entries for this day.</p>
                        <p className="text-xs text-gray-400 mt-1">Start by typing what you ate!</p>
                      </div>
                    </div>
                  ) : (                    <div 
                      ref={scrollContainerRef}
                      className="h-full overflow-y-auto custom-scrollbar-ai-assistant pr-2"
                      style={{ pointerEvents: 'auto' }}
                    >
                      <FoodLogList
                        foodLog={foodLog}
                        editMealId={editMealId}
                        editMealData={editMealData}
                        handleEdit={handleEdit}
                        handleEditChange={handleEditChange}
                        handleEditSave={handleEditSave}
                        handleEditCancel={handleEditCancel}
                        handleDelete={handleDelete}
                        handleMacroEdit={handleMacroEdit}
                      />
                    </div>
                  )}
                </div>
              </div>              {/* Input Bar - Fixed at Bottom */}
              <div className="border-t border-gray-100 pt-4 flex-shrink-0">
                <InputBar
                  prompt={prompt}
                  setPrompt={setPrompt}
                  handlePromptSubmit={handlePromptSubmit}
                  handleBarcodeScan={() => setShowBarcodeModal(true)}
                  handleGallery={handleGallery}
                  handleCamera={handleCamera}
                  fileInputRef={fileInputRef}
                  handleImageUpload={handleImageUpload}
                  setShowScanHistory={setShowScanHistory}
                />
              </div>
            </div>
          </div>
        </div>
      </div>      {/* Custom Styles */}
      <style jsx>{`
        .custom-scrollbar-minimal::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar-minimal::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar-minimal::-webkit-scrollbar-thumb {
          background: #10b981;
          border-radius: 10px;
        }
        .custom-scrollbar-minimal::-webkit-scrollbar-thumb:hover {
          background: #059669;
        }
        
        /* AI Assistant Scrollbar */
        .custom-scrollbar-ai-assistant::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar-ai-assistant::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar-ai-assistant::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #10b981, #059669);
          border-radius: 10px;
        }
        .custom-scrollbar-ai-assistant::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #059669, #047857);
        }
        
        /* Hide scrollbar for Firefox */
        .custom-scrollbar-minimal {
          scrollbar-width: thin;
          scrollbar-color: #10b981 transparent;
        }
        
        .custom-scrollbar-ai-assistant {
          scrollbar-width: thin;
          scrollbar-color: #10b981 #f1f5f9;
        }
        
        /* Responsive design improvements */
        @media (max-width: 1024px) {
          .lg\\:w-1\\/2 {
            width: 100%;
            min-height: 50vh;
          }
          
          .h-\\[calc\\(100vh-8rem\\)\\] {
            height: calc(100vh - 12rem);
          }
        }
        
        @media (max-width: 768px) {
          .space-y-6 > *:not(:last-child) {
            margin-bottom: 1rem;
          }
          
          .h-\\[calc\\(100vh-8rem\\)\\] {
            height: calc(100vh - 10rem);
          }
        }
        
        @media (max-width: 640px) {
          .h-\\[calc\\(100vh-8rem\\)\\] {
            height: 60vh;
          }
        }
      `}</style>

      {/* Manual Entry Form */}
      {manualEntry && (
        <ManualEntryForm
          manualEntry={manualEntry}
          handleManualSubmit={handleManualSubmit}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
